import { useState } from "react";
import {
  Button,
  Dialog,
  Select,
  TextArea,
  TextField,
} from "@jkast90/kast-common/ui";

const FORM_ID = "edit-wish-form";

interface WishData {
  id?: string;
  description: string;
  link?: string;
  category?: string;
  priority?: string;
  price?: number;
  size?: string;
  quantity?: number;
}

interface EditWishDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: WishData) => void;
  wishData?: WishData;
  isNew?: boolean;
}

// Helper function to extract URL from pasted text (e.g., Amazon share text)
function extractUrlFromText(text: string): string {
  // Common URL pattern matching
  const urlRegex = /https?:\/\/[^\s]+/gi;
  const matches = text.match(urlRegex);

  if (matches && matches.length > 0) {
    // Return the first URL found, cleaned up
    return matches[0].replace(/[.,;!?]+$/, ""); // Remove trailing punctuation
  }

  return text; // Return original text if no URL found
}

export default function EditWishDialog({
  open,
  onClose,
  onSave,
  wishData,
  isNew = false,
}: EditWishDialogProps) {
  const [formData, setFormData] = useState<WishData>({
    description: "",
    link: "",
    category: "",
    priority: "medium",
    price: undefined,
    size: "",
    quantity: 1,
  });

  // Reset the form when the wish or the open state changes, during render
  // rather than in an effect so it never paints with stale values.
  const [prev, setPrev] = useState<{
    wishData?: WishData;
    open: boolean;
  } | null>(null);
  if (!prev || prev.wishData !== wishData || prev.open !== open) {
    setPrev({ wishData, open });
    if (wishData) {
      setFormData(wishData);
    } else {
      setFormData({
        description: "",
        link: "",
        category: "",
        priority: "medium",
        price: undefined,
        size: "",
        quantity: 1,
      });
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "price"
          ? value
            ? parseFloat(value)
            : undefined
          : name === "quantity"
            ? value
              ? parseInt(value)
              : 1
            : value,
    }));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("text");

    // Check if pasted text contains a URL (likely Amazon share text or similar)
    if (pastedText.includes("http")) {
      e.preventDefault(); // Prevent default paste behavior
      const extractedUrl = extractUrlFromText(pastedText);

      setFormData((prev) => ({
        ...prev,
        link: extractedUrl,
      }));
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formData.description.trim()) {
      return;
    }
    onSave(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isNew ? "Add Wish" : "Edit Wish"}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form={FORM_ID}>
            {isNew ? "Add" : "Save"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="kui-form-grid">
        <TextArea
          wrapperClassName="kui-form-span"
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          rows={3}
          placeholder="What do you want?"
        />
        <TextField
          wrapperClassName="kui-form-span"
          label="Link"
          type="url"
          name="link"
          value={formData.link || ""}
          onChange={handleChange}
          onPaste={handlePaste}
          placeholder="https://... (paste Amazon share text and it will extract the URL)"
        />
        <TextField
          label="Category"
          name="category"
          value={formData.category || ""}
          onChange={handleChange}
          placeholder="e.g. Books, Tech, Clothing"
        />
        <Select
          label="Priority"
          name="priority"
          options={[
            { value: "low", label: "Low" },
            { value: "medium", label: "Medium" },
            { value: "high", label: "High" },
          ]}
          value={formData.priority || "medium"}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, priority: value ?? "medium" }))
          }
        />
        <TextField
          label="Price ($)"
          type="number"
          name="price"
          value={formData.price || ""}
          onChange={handleChange}
          placeholder="0.00"
          step="0.01"
          min="0"
        />
        <TextField
          label="Size"
          name="size"
          value={formData.size || ""}
          onChange={handleChange}
          placeholder="e.g. Medium, XL, 10"
        />
        <TextField
          label="Quantity"
          type="number"
          name="quantity"
          value={formData.quantity || 1}
          onChange={handleChange}
          min="1"
        />
      </form>
    </Dialog>
  );
}
